import { CARE_SIGNAL_VIEWS } from '@troyai/portal/health-timeline/data-access';
import { isScriptedEventUuid, isScriptedId } from '@troyai/portal/investor-demo/core';

/**
 * Who answers a request about the demo member. She is a real member, so most of her requests go
 * to the real backend; the scripted objects are answered from the demo store, and a few lists
 * hold both kinds, so the two answers are merged.
 */

/** The lists that hold real and scripted rows together. */
export type MergeKind =
  'episodes' | 'assessments' | 'reviews' | 'story' | 'signals' | 'visits' | 'versions';

export type MemberRequestKind =
  { type: 'scripted' } | { type: 'real' } | { type: 'merge'; merge: MergeKind };

const scripted = (): MemberRequestKind => ({ type: 'scripted' });
const real = (): MemberRequestKind => ({ type: 'real' });
const merge = (kind: MergeKind): MemberRequestKind => ({ type: 'merge', merge: kind });

/** A whole-number path segment in the scripted id range (`'candidates'` and `'77'` are not). */
function isScriptedSegment(segment: string | undefined): boolean {
  return segment !== undefined && /^\d+$/.test(segment) && isScriptedId(Number(segment));
}

/**
 * Classifies one request. `rest` is the path after `members/<id>`; `query` reads a request
 * parameter. The lists and the by-id rules are matched by their first segment; anything the
 * rules do not name is real.
 */
export function classifyMemberRequest(
  method: string,
  rest: string[],
  query: (name: string) => string | null
): MemberRequestKind {
  const get = method.toUpperCase() === 'GET';
  const [head, second] = rest;
  const bare = rest.length === 1;

  switch (head) {
    case 'intervention-episodes':
    case 'assessments':
      if (bare) {
        return get ? merge(head === 'assessments' ? 'assessments' : 'episodes') : real();
      }
      return isScriptedSegment(second) ? scripted() : real();

    case 'recommendation-reviews':
      return get && bare ? merge('reviews') : real();

    case 'health-summary':
      return get && bare ? scripted() : real();

    case 'health-story':
      if (bare) {
        if (!get) {
          return real();
        }
        return query('category') === 'interaction' ? scripted() : merge('story');
      }
      return second !== undefined && isScriptedEventUuid(second) ? scripted() : real();

    case 'timeline': {
      const view = query('view');
      const signal = view !== null && (CARE_SIGNAL_VIEWS as readonly string[]).includes(view);
      return get && bare && signal ? merge('signals') : real();
    }

    case 'visits': {
      // Only the first page holds the scripted visits; later pages are all real.
      const offset = query('offset');
      return get && bare && (offset === null || Number(offset) === 0) ? merge('visits') : real();
    }

    case 'clinical-event-versions':
      return get && bare ? merge('versions') : real();

    default:
      return real();
  }
}

/**
 * Splits the `ids` of a clinical-event-versions request into scripted version ids (answered
 * here) and real ones (asked of the backend). Blanks and non-numbers are dropped.
 */
export function splitVersionIds(ids: string | null): { scripted: number[]; real: number[] } {
  const split: { scripted: number[]; real: number[] } = { scripted: [], real: [] };
  for (const part of (ids ?? '').split(',')) {
    const text = part.trim();
    const id = Number(text);
    if (text === '' || !Number.isFinite(id)) {
      continue;
    }
    (isScriptedId(id) ? split.scripted : split.real).push(id);
  }
  return split;
}
